import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowRight, FiCheckCircle, FiZap, FiExternalLink, FiSend } from 'react-icons/fi';
import './Offer.css';
import axios from 'axios';

const Offer = () => {
  const { offerId, refer } = useParams();
  const navigate = useNavigate();
  const [vpa, setVpa] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [offerDetails, setOfferDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deviceData, setDeviceData] = useState({});
  const [globalSettings, setGlobalSettings] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // First fetch global settings
        const settingsResponse = await axios.post('https://offers.cashbolt.in.net/server/', {
          action: 'getGlobalSettings'
        });

        if (settingsResponse.data.status) {
          setGlobalSettings(settingsResponse.data.data);
        }

        // Then fetch offer details
        const offerResponse = await axios.post('https://offers.cashbolt.in.net/server/', {
          action: 'getOfferDetails',
          offerId: offerId
        });

        if (offerResponse.data.status) {
          setOfferDetails(offerResponse.data.data);

        } else {
          throw new Error(offerResponse.data.message || 'Offer not found');
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    const collectDeviceData = () => {
      const data = {
        clickTime: new Date().toISOString(),
        deviceIP: '',
        platform: navigator?.platform || 'unknown',
        browser: navigator?.userAgent || 'unknown',
        userAgent: navigator?.userAgent || 'unknown',
        deviceType: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
        connectionType: navigator?.connection?.effectiveType || 'unknown',
        country: '',
        region: '',
        city: '',
        source: document.referrer || 'direct',
        campaign: new URLSearchParams(window.location.search).get('utm_campaign') || ''
      };
      setDeviceData(data);
    };

    fetchData();
    collectDeviceData();

    // Get GAID if available
    if (window.getGAID) {
      window.getGAID().then(gaid => {
        localStorage.setItem('gaid', gaid);
      });
    }
  }, [offerId, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!offerDetails) return;
    // Check the structure in browser console
    try {
      const trackingData = {
        action: 'trackClick',
        offerId: offerId,
        user: vpa,
        refer: refer || 'direct',
        clickTime: deviceData.clickTime,
        gaid: localStorage.getItem('gaid') || '',
        platform: deviceData.platform,
        browser: deviceData.browser,
        userAgent: deviceData.userAgent,
        deviceType: deviceData.deviceType,
        connectionType: deviceData.connectionType,
        source: deviceData.source,
        referrer: document.referrer || '',
        campaign: deviceData.campaign
      };

      const resp = await axios.post('https://offers.cashbolt.in.net/server/', trackingData);
      if (resp.data.status === true) {
        setSubmitted(true);
        setTimeout(() => {
          redirectTo(resp.data.track);
        }, 3000);
        return;
      }

    } catch (err) {
      console.error("Submission error:", err);
      alert('Error submitting VPA');
    }
  };

  const redirectTo = (url) => {
    window.location.href = url;
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-pulse"></div>
      </div>
    );
  }

  if (error || !offerDetails) {
    return (
      <div className="error-screen">
        <div className="error-card">
          <h2>Offer Not Found</h2>
          <p>The offer you're looking for doesn't exist or may have expired.</p>

          <div className="error-buttons">
            <motion.button
              onClick={() => navigate('/')}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="browse-offers-btn"
            >
              Browse Offers
            </motion.button>

            <motion.button
              onClick={() => window.open(globalSettings.tgLink, '_blank')}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="telegram-btn"
            >
              <FiSend /> Join Telegram
            </motion.button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-page">
      <motion.div
        className="bg-shine"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.1 }}
        transition={{ duration: 1.5 }}
      />

      <motion.div
        className="payment-container"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="offer-header">
          <motion.div
            className="offer-logo"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            {offerDetails.name.charAt(0)}
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h1>{offerDetails.name}</h1>
            <p className="offer-subtitle">Enter your payment details</p>
          </motion.div>
        </div>

        <div className="payment-section">
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <div className="input-wrapper">
              <div className="input-container">
                <label htmlFor="vpa">Your Votex VPA</label>
                <motion.div className="input-with-amount" whileHover={{ y: -2 }}>
                  <input
                    type="text"
                    id="vpa"
                    autoComplete='off'
                    value={vpa}
                    onChange={(e) => setVpa(e.target.value)}
                    placeholder={offerDetails.vpaExample || 'yourname@upi'}
                    required
                  />
                  <motion.div
                    className="amount-display"
                    animate={{
                      background: [
                        'linear-gradient(135deg, rgba(74, 222, 128, 0.15), rgba(74, 222, 128, 0.25))',
                        'linear-gradient(135deg, rgba(74, 222, 128, 0.25), rgba(74, 222, 128, 0.35))',
                        'linear-gradient(135deg, rgba(74, 222, 128, 0.15), rgba(74, 222, 128, 0.25))'
                      ]
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity
                    }}
                  >
                    ₹{offerDetails.payout || '₹0'}
                  </motion.div>
                </motion.div>
              </div>

              <motion.button
                type="submit"
                className="submit-btn"
                whileHover={{
                  y: -2,
                  boxShadow: '0 8px 25px rgba(139, 92, 246, 0.5)'
                }}
                whileTap={{ scale: 0.98 }}
              >
                Complete Offer <FiArrowRight />
              </motion.button>
            </div>
          </motion.form>

          <motion.div
            className="wallet-cta"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <div className="wallet-content">
              <h3>Instant Payouts</h3>
              <p>Use CBPocket for faster rewards</p>
            </div>
            <motion.button
              className="wallet-btn"
              whileHover={{ y: -1 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => redirectTo('Https://CBPocket.site/')}
            >
              <FiZap /> Get Wallet
            </motion.button>
          </motion.div>

          <motion.div
            className="telegram-cta"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
          >
            <div className="telegram-content">
              <h3>Need Help?</h3>
              <p>Join our Telegram community</p>
            </div>
            <motion.button
              className="telegram-btn"
              whileHover={{ y: -1 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => window.open(globalSettings.tgLink, '_blank')}
            >
              <FiSend /> Join Now
            </motion.button>
          </motion.div>
        </div>

        <motion.div
          className="terms-container"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
        >
          <div className="terms-header">
            <h3>Terms & Conditions</h3>
            <div className="divider"></div>
          </div>
          <motion.div
            className="terms-list-inline"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1 }}
          >
            {offerDetails.terms && typeof offerDetails.terms === 'string' ? (
              <>
                {offerDetails.terms.split(',').map((feature, index, array) => (
                  <React.Fragment key={index}>
                    <motion.span
                      className="term-item"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{
                        delay: 1.1 + index * 0.05,
                        type: "spring",
                        stiffness: 100
                      }}
                    >
                      {feature.trim()}
                    </motion.span>
                    {index < array.length - 1 && (
                      <span className="term-separator">
                      
                      </span>
                    )}
                  </React.Fragment>
                ))}
              </>
            ) : (
              <span className="term-item">No terms available</span>
            )}
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Track Lead Button */}
      <motion.button
        className="track-lead-btn"
        onClick={() => navigate('/track')}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.2 }}
        whileHover={{
          x: -5,
          boxShadow: '0 5px 15px rgba(139, 92, 246, 0.4)'
        }}
        whileTap={{ scale: 0.95 }}
      >
        <FiExternalLink /> Track Lead
      </motion.button>

      <AnimatePresence>
        {submitted && (
          <motion.div
            className="success-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="success-card"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', damping: 20 }}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2 }}
              >
                <FiCheckCircle className="success-icon" />
              </motion.div>
              <h2>Offer Track Started!</h2>
              <p>We'll process your reward to:</p>
              <p className="upi-id">{vpa}</p>
              <p className="success-terms">Redirecting In 3!!!</p>

              <div className="progress-container">
                <motion.div
                  className="progress-bar"
                  initial={{ width: 0 }}
                  animate={{ width: '100%' }}
                  transition={{ duration: 2.5, delay: 0.3 }}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Offer;